/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {PackageCategoryEntity} from "types/entities/PackageCategoryEntity";
import {PackageCategory} from "types/enums/PackageCategory";
import {useTranslation} from "react-i18next";
import BookingAction from "store/booking/BookingAction";
import {appendQueryParams} from "utils/utility";
import {ThunkAction} from "redux-thunk";
import {useDispatch} from "useDispatch";
import {useHistory} from "react-router-dom";
import {useTypedSelector} from "helpers/reducers";
import {
    FERIENPASS_MAXIMUM_DATE,
    FERIENPASS_MINIMUM_DATE,
    IS_FERIENSPASS_ENABLED,
    MAX_MONTH_COUNT_FOR_AVAILABLE_DATES
} from "helpers/config";
import {PersonType} from "types/enums/PersonType";
import clsx from "clsx";

export const usePackageCategory = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const validUntil = "22.05.2026";

    const categories: PackageCategoryEntity[] = [
        {
            id: PackageCategory.General,
            title: t("Arbo Tickets"),
            description: (
                <div>
                    <b
                        dangerouslySetInnerHTML={{
                            __html: t("With the Arbo Ticket, the park is yours to explore: over 20 attractions, as often as you like. "),
                        }}
                    />
                    <br />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t("Simply choose your time – from a quick evening session to a full day. "),
                        }}
                    />
                    <br />
                    <b
                        dangerouslySetInnerHTML={{
                            __html: t("And when the Speed & Drift Zone calls, top up your credits and go full throttle."),
                        }}
                    />
                    <br />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li className={"font-bold"}>{t("Quick Access")}</li>
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Group photo via email")}} />
                    </ul>
                    {/*<span*/}
                    {/*    dangerouslySetInnerHTML={{*/}
                    {/*        __html: t("The perfect option for speed junkies who want to take advantage of KartMania."),*/}
                    {/*    }}*/}
                    {/*/>*/}
                </div>
            ),
            timeAvailableText: t("2, 4 or 6 hours"),

            images: ["/img/new_category/standard_1.webp", "/img/new_category/standard_2.webp", "/img/new_category/standard_3.webp"],
            color: "red-400",
            price: 29,
            shortTitle: t("Your direct route to over 20 attractions: however you want. as often as you want."),
            tags: [
                {iconName: "calendar", title: t("Monday till Sunday")},
                {iconName: "persons", title: t("personsminimum", {count: 1})},
                // {iconName: "percent", title: t("discount_from_person", {count: 10})},
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("Book now"),
            showMoreDetail: false,
            priceImages: [
                {
                    image: "/img/new_category/details/gelb.webp",
                    alt: "",
                },
                {
                    image: "/img/new_category/details/rot.webp",
                    alt: "",
                },
            ],
            extra: {
                minDaysFromNow: 0,
                maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
            },
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available."),
                    t("Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)"),
                    t("Please note that cash payments are not possible on site. Attractions cannot be reserved."),
                ],
                infoValues: [
                    // {
                    //     key: t("from number persons", {count: 10}),
                    //     value: t("one person free"),
                    // },
                    // {
                    //     key: t("from number persons", {count: 20}),
                    //     value: t("two persons free"),
                    // },
                    //     {
                    //         key: t("from number persons", {count: 30}),
                    //         value: t("three persons free"),
                    //     },
                    //     {
                    //         key: t("etc..."),
                    //         value: "",
                    //     },
                ],
            },
            enable: true,
            deprecated: false,
        },
        {
            id: PackageCategory.Action,
            title: "Family Tickets",
            description: (
                <div>
                    <b
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "With the Family Ticket, children enjoy half-price rides, and the whole family can experience over 20 attractions together. "
                            ),
                        }}
                    />
                    <br />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t("If the Speed & Drift Zone is calling, you can purchase additional credits and share them as a family."),
                        }}
                    />

                    <br />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t("With the exclusive large credit packs, you can save up to 50% per ride."),
                        }}
                    />
                    <br />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t("From Sunday to Thursday and on public holidays, the park is yours to enjoy."),
                        }}
                    />
                    <br />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li className={"font-bold"}>{t("Quick Access")}</li>
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Group photo via email")}} />

                        <li dangerouslySetInnerHTML={{__html: t("Up to 50% off: exclusive access to the large credit packs for the Speed & Drift Zone")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Shared credit balance — credits transferable within the family")}} />
                    </ul>
                </div>
            ),
            timeAvailableText: t("2, 4 or 6 hours"),

            images: ["/img/new_category/family_1.webp", "/img/new_category/family_2.webp", "/img/new_category/family_3.webp"],
            color: "purple-400",
            price: 29.5,
            shortTitle: "Ein Ticket für die ganze Familie. Kinder zahlen nur den halben Preis.",

            tags: [
                {iconName: "persons", title: t("personsmin_max", {min: 2, max: 6})},
                {iconName: "persons", title: t("Minimum 1 adult")},
                {iconName: "calendar", title: t("Sunday till Thursday + Holidays")},
                {iconName: "percent", title: t("discount_for_children", {percent: 50})},
            ],
            buttonText: t("Book now"),
            showMoreDetail: false,
            priceImages: [
                {
                    image: "/img/new_category/details/gelb.webp",
                    alt: "",
                },
                {
                    image: "/img/new_category/details/rot.webp",
                    alt: "",
                },
            ],
            extra: {
                minDaysFromNow: 0,
                maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
            },
            categoryInfo: {
                upsell_percent: 0,
                title: t("Family offer"),
                subtitle: t("Families can now visit us even cheaper!"),
                infoText: [
                    t("Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available."),
                    t("Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)"),
                    t("Please note that cash payments are not possible on site. Attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("All children aged 8 to 15 years get 25% discount"),
                        value: "",
                    },
                    {
                        key: t("The Family Ticket is available in two versions: red and yellow"),
                        value: "",
                    },
                    {
                        key: t("With and without karting possible in the same reservation"),
                        value: "",
                    },
                    {
                        key: t("The ticket is valid on Sunday and Wednesday"),
                        value: "",
                    },
                    {
                        key: t("Min. 1 adult (at regular rate), max. 6 people"),
                        value: "",
                    },
                ],
            },
            enable: true,
            deprecated: false,
        },
        {
            id: PackageCategory.Ferien,
            title: t("Ferien-Spass Offer"),
            description: (
                <div>
                    <ul>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t("Smart holiday offers until", {date: validUntil}),
                            }}
                        ></li>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t("This _season, parents especially benefit from our flexible holiday packages at ArboPark.", {
                                    season: t("Sports-Holidays"),
                                }),
                            }}
                        ></li>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    "With % discount for kids until 18 Years, FREE entry for accompanying persons and even active childcare on weekdays.",
                                    {percent: 20}
                                ),
                            }}
                        ></li>

                        <br />
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    "Look forward to stress-free days full of action, fun and movement and simply choose the offer that suits your holiday plans!"
                                ),
                            }}
                        ></li>
                    </ul>
                </div>
            ),
            timeAvailableText: t("2, 4 or 6 hours"),

            images: [
                "/img/new_category/Ferien/Ferien-Spass_Angebote_Bild1.jpg",
                "/img/new_category/Ferien/Ferien-Spass_Angebote_Bild2.jpg",
                "/img/new_category/Ferien/Ferien-Spass-Angebote_Bild3.jpg",
            ],
            color: "yellow-400",
            price: 31.2,
            shortTitle: t("Special offer for the _SEASON", {season: t("Sports-Holidays")}),
            tags: [
                {iconName: "calendar", title: t("Monday till Friday")},
                {iconName: "persons", title: t("personsminimum", {count: 1})},
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("View more"),
            showMoreDetail: false,
            extra: {
                minDaysFromNow: 0,
                maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
            },
            enable: IS_FERIENSPASS_ENABLED,
            categoryInfo: null,
            deprecated: false,
        },
        {
            id: PackageCategory.Event,
            title: t("Group packages"),
            description: (
                <div>
                    <span>
                        <b>{t("Planning an unforgettable experience for your group? ")}</b>
                    </span>
                    <span>
                        <b>
                            {t(
                                "Whether it's a birthday party, stag/hen party, corporate event, school trip, or just because: from Mega Fun to Happy Party VIP, we have the perfect package for every occasion. "
                            )}
                        </b>
                    </span>
                    <span>{t("A reserved food and beverage area, easy access, priority check-in, and welcome hosting are already included. ")}</span>
                    <span>
                        {t(
                            "The PLUS & VIP packages also include a welcome drink and even more action: compete against each other in challenges and experience your day as a true group adventure. "
                        )}
                    </span>
                    <span>
                        <b>{t("And if you want to go full throttle, the Speed & Drift Zone awaits you with optional credits.")}</b>
                    </span>
                </div>
            ),

            timeAvailableText: t("2, 3, 4 or 6 hours"),
            images: ["/img/new_category/events_1.webp", "/img/new_category/events_2.webp", "/img/new_category/events_3.webp"],
            color: "yellow-400",
            price: 29.75,
            shortTitle: t("Your occasion. Your experience. Your perfect package."),
            tags: [
                {iconName: "calendar", title: t("Monday till Sunday")},
                {iconName: "persons", title: t("personsminimum", {count: 4})},
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("View more"),
            showMoreDetail: false,
            extra: {
                minDaysFromNow: 0,
                maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
            },
            enable: true,
            categoryInfo: null,
            deprecated: false,
        },
        {
            id: PackageCategory.Giftcard,
            title: t("Gift voucher"),
            description: t(
                "Give the gift of pure pleasure with the ArboPark gift voucher, entry to exciting experiences in the largest indoor leisure park in Switzerland."
            ),
            timeAvailableText: t("2, 4 or 6 hours"),

            images: ["/img/package_category/gutschein.webp", "/img/package_category/gutschein_2.webp"],
            color: "green-400",
            price: 40,
            shortTitle: t("Your ticket to unforgettable activities"),
            tags: [],
            buttonText: t("Buy now"),
            showMoreDetail: false,
            enable: true,
            categoryInfo: null,
            deprecated: false,
        },
        {
            id: PackageCategory.Yearly,
            title: t("Arbo- Abo's"),
            description: (
                <div>
                    <div dangerouslySetInnerHTML={{__html: t("With our subscriptions")}} />
                </div>
            ),
            timeAvailableText: t("2, 4 or 6 hours"),
            images: ["/img/new_category/Jahr.webp", "/img/new_category/Halb.webp"],
            color: "green-400",
            price: 295,
            shortTitle: t("Constant fun for less money"),
            tags: [
                {iconName: "calendar", title: t("Wednesday till Sunday")},
                {iconName: "persons", title: t("personsminimum", {count: 1})},
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("Buy now"),
            showMoreDetail: false,
            enable: false,
            categoryInfo: null,
            deprecated: true,
        },
    ];

    const ferienSubcategories: PackageCategoryEntity[] = [
        {
            id: PackageCategory.FerienPass,
            title: t("Buddy Ferien-Spass"),
            description: (
                <div>
                    <h3>
                        <b>{t("The perfect holiday fun for large children's groups")}</b>
                    </h3>
                    <ul className={"list-disc pl-5"}>
                        <li>
                            <b>{t("Bookable from 4 people")}</b>
                        </li>
                        <li>
                            <b>{t("2, 4 or 6 hours tickets")}</b>, {t("with or without karting")}
                        </li>
                        <li>
                            {t("One teamleader receives")} <b>{t("a free entry")}</b>
                        </li>
                        <li>
                            {t("Children under 18 years of age receive")}&nbsp;
                            <b>{t("% discount on the ticket price", {percent: 20})}</b>
                        </li>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t("Available until {validUntil}", {validUntil: validUntil}),
                            }}
                        ></li>
                    </ul>
                </div>
            ),
            timeAvailableText: t("2, 4 or 6 hours"),
            images: [
                "/img/new_category/Ferien/Buddy/Buddy_Bild1.jpg",
                "/img/new_category/Ferien/Buddy/Buddy_Bild2.jpg",
                "/img/new_category/Ferien/Buddy/Buddy_Bild3.jpg",
            ],
            color: "red-400",
            price: 31.2,
            shortTitle: t("Special offer for the _SEASON", {season: t("Sports-Holidays")}),
            tags: [
                {iconName: "calendar", title: t("Monday till Friday")},
                {iconName: "persons", title: t("personsminimum", {count: 4})},
                {iconName: "percent", title: t("Team leader gratis")},
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("Book now"),
            showMoreDetail: false,
            priceImages: [
                {
                    image: "/img/new_category/details/gelb.webp",
                    alt: "",
                },
                {
                    image: "/img/new_category/details/rot.webp",
                    alt: "",
                },
            ],
            extra: {
                minDaysFromNow: 0,
                maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                releaseDate: FERIENPASS_MINIMUM_DATE,
                maximumDate: FERIENPASS_MAXIMUM_DATE,
            },
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available."),
                    t("Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)"),
                    t("Please note that cash payments are not possible on site. Attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("All children aged 8 to 17 years get % discount", {percent: 20}),
                        value: "",
                    },
                    {
                        key: t("The Ferienpass is available in two versions: red and yellow"),
                        value: "",
                    },
                    {
                        key: t("With and without karting possible in the same reservation"),
                        value: "",
                    },
                    {
                        key: t("The ticket is valid Monday till Friday"),
                        value: "",
                    },
                    {
                        key: t("Min. 1 adult"),
                        value: "",
                    },
                ],
            },
            enable: IS_FERIENSPASS_ENABLED,
            deprecated: false,
        },
        {
            id: PackageCategory.FerienPassB,
            title: t("Easy Ferien-Spass"),
            description: (
                <div>
                    <h3>
                        <b> {t("You drop off – we take over: Our active childcare during _season holidays", {season: t("Sports-Holidays")})}</b>
                    </h3>
                    <ul className={"list-disc pl-5"}>
                        <li>
                            <b>{t("Monday till Friday, at 14`o clock")}</b>
                        </li>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t("% discount for all children under __years", {percent: 20, years: 12}),
                            }}
                        ></li>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t("For childcare you pay only 5 CHF per child"),
                            }}
                        ></li>
                        <li>{t("Children's groups can be registered in advance by phone")}</li>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t("Available until {validUntil}", {validUntil: validUntil}),
                            }}
                        ></li>
                    </ul>
                </div>
            ),
            timeAvailableText: t("2, 4 or 6 hours"),
            images: [
                "/img/new_category/Ferien/Easy/Easy_Bild1.jpg",
                "/img/new_category/Ferien/Easy/Easy_Bild2.jpg",
                "/img/new_category/Ferien/Easy/Easy_Bild3.jpg",
            ],
            color: "red-400",
            price: 36.2,
            shortTitle: t("Special offer for the _SEASON", {season: t("Sports-Holidays")}),
            tags: [
                {iconName: "calendar", title: t("Monday till Friday")},
                {iconName: "persons", title: t("personsminimum", {count: 1})},
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("Book now"),
            showMoreDetail: false,
            priceImages: [
                {
                    image: "/img/new_category/details/gelb.webp",
                    alt: "",
                },
                {
                    image: "/img/new_category/details/rot.webp",
                    alt: "",
                },
            ],
            extra: {
                minDaysFromNow: 0,
                maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                releaseDate: FERIENPASS_MINIMUM_DATE,
                maximumDate: FERIENPASS_MAXIMUM_DATE,
            },
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available."),
                    t("Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)"),
                    t("Please note that cash payments are not possible on site. Attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("All children aged 8 to 17 years get % discount", {percent: 20}),
                        value: "",
                    },
                    {
                        key: t("The Ferienpass is available in two versions: red and yellow"),
                        value: "",
                    },
                    {
                        key: t("With and without karting possible in the same reservation"),
                        value: "",
                    },
                    {
                        key: t("The ticket is valid Monday till Friday"),
                        value: "",
                    },
                    {
                        key: t("Children are accompanied by adult person from Arbo Park personal"),
                        value: "",
                    },
                ],
            },
            enable: IS_FERIENSPASS_ENABLED,
            deprecated: false,
        },
        {
            id: PackageCategory.ThreeTwo,
            title: t("3-for-2 Ferien-spass"),
            description: (
                <div>
                    <h3>
                        <b>Für alle, die in den Sommerferien nicht genug bekommen können:</b>
                    </h3>

                    <ul className={"list-disc pl-5"}>
                        <li className={"text-arbo-red font-bold"}>Gilt für 1 Person und 3 separate Besuche</li>
                        <li>
                            Beim Kauf von 3 Tickets, <b>1 x Gratis-Eintritt erhalten</b>
                        </li>
                        <li>
                            Gilt <b>Montag bis Freitag</b>
                        </li>
                        <li>
                            <b>2, 4 oder 6 Stunden Tickets</b>, mit oder ohne Karting
                        </li>
                        <li>
                            Nur <b>bis {validUntil}</b> erhältlich
                        </li>
                    </ul>
                </div>
            ),
            timeAvailableText: t("2, 4 or 6 hours"),
            images: [
                "/img/new_category/Ferien/3for2/3for2_Bild1.webp",
                "/img/new_category/Ferien/3for2/3for2_Bild2.webp",
                "/img/new_category/Ferien/3for2/3for2_Bild3.webp",
            ],
            color: "green-400",
            price: 78,
            shortTitle: t("Special offer for the _SEASON", {season: t("Sports-Holidays")}),
            tags: [
                {iconName: "calendar", title: t("Monday till Friday")},
                {iconName: "persons", title: t("personsminimum", {count: 1})},
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("Buy now"),
            showMoreDetail: false,
            enable: false,
            categoryInfo: null,
            deprecated: true,
        },
    ];

    const eventsSubcategories: PackageCategoryEntity[] = [
        {
            id: PackageCategory.MegaFun,
            title: t("Mega Fun"),
            description: (
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "Mega Fun turns a park visit into a group outing that runs smoothly. You enter faster with Priority Check-in and have your own reserved table in the gastro area. Perfect for everyone who wants more togetherness — the party starts from four people."
                            ),
                        }}
                    />

                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Reserved zone in the gastro area")}} />
                        <li className={"font-bold"}>{t("Welcome hosting")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />
                    </ul>
                </div>
            ),
            images: ["/img/new_category/megafun_1.webp", "/img/new_category/megafun_2.webp", "/img/new_category/megafun_3.webp"],
            color: "red-400",
            price: 64,
            shortTitle: t("Your group upgrade with priority check-in and welcome hosting."),
            tags: [
                {iconName: "persons", title: t("From 4 people")},
                {iconName: "calendar", title: t("Bookable Monday to Sunday")},
                {iconName: "activities", title: t("20+ attractions")},
            ],
            buttonText: t("Book now"),
            timeAvailableText: t("3, 4.5 or 8 hours"),
            showMoreDetail: false,
            enable: true,
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark."),
                    t("Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF."),
                    t("Please note that cash payment is not possible on-site, and attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("Gruppengrösse"),
                        value: t("Ab 4 Personen"),
                    },
                    {
                        key: t("Check-in"),
                        value: t("Easy Access & Priority Check-in"),
                    },
                    {
                        key: t("Schliessfächer"),
                        value: t("Kostenlose Schliessfächer"),
                    },
                    {
                        key: t("Attraktionen"),
                        value: t("Unbegrenzter Zugang zu allen Attraktionen ausser der Speed & Drift Zone"),
                    },
                    {
                        key: t("Getränk"),
                        value: t("Welcome-Drink (alkoholfrei)"),
                    },
                    {
                        key: t("Gastrobereich"),
                        value: t("Reservierte Zone im Gastrobereich"),
                    },
                    {
                        key: t("Hosting"),
                        value: t("Welcome-Hosting"),
                    },
                    {
                        key: t("Gewinnspiel"),
                        value: t("Kostenlose Teilnahme am Gewinnspiel am Ende des Besuchs"),
                    },
                    {
                        key: t("Gruppenfoto"),
                        value: t("Ausgedrucktes Gruppenfoto sowie Versand per E-Mail"),
                    },
                    {
                        key: t("Verfügbarkeit"),
                        value: t("Montag bis Sonntag"),
                    },
                    {
                        key: t("Credits"),
                        value: t("Nicht verbrauchte Credits bleiben für den nächsten Besuch erhalten"),
                    },
                    {
                        key: t("Übertragbarkeit"),
                        value: t("Credits sind personengebunden und nicht auf andere Personen übertragbar"),
                    },
                ],
            },
            deprecated: false,
            creditBased: true,
            extra: {
                releaseDate: "2026-08-19",
            },
        },
        {
            id: PackageCategory.GigaFun,
            title: t("Giga Fun"),
            description: (
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "With Giga Fun you start with the perfect group package: a welcome drink to kick things off, your own gastro area, and one Category A competition are already included. Whether Grand Prix Classic, Interactive Mini Golf or Cube Challenge – you compete against each other and experience your first shared contest. From eight people, your experience becomes truly big."
                            ),
                        }}
                    />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />

                        <li>{t("Welcome drink (non-alcoholic)")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Reserved zone in the gastro area")}} />

                        <li dangerouslySetInnerHTML={{__html: t("One Category A competition included")}} />

                        <li className={"font-bold"}>{t("Welcome hosting")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />
                    </ul>
                </div>
            ),
            images: ["/img/new_category/gigafun_1.webp", "/img/new_category/gigafun_2.webp", "/img/new_category/gigafun_3.webp"],
            color: "red-400",
            price: 75,
            shortTitle: t("Large group, big program — including your first competition."),
            tags: [
                {iconName: "persons", title: t("From 8 people")},
                {iconName: "calendar", title: t("Monday to Sunday")},
                {iconName: "activities", title: t("20+ attractions")},
            ],
            buttonText: t("Book now"),
            timeAvailableText: t("3, 4.5 or 8 hours"),
            showMoreDetail: false,
            enable: true,
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark."),
                    t("Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF."),
                    t("Please note that cash payment is not possible on-site, and attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("Gruppengrösse"),
                        value: t("Ab 8 Personen"),
                    },
                    {
                        key: t("Check-in"),
                        value: t("Easy Access & Priority Check-in"),
                    },
                    {
                        key: t("Schliessfächer"),
                        value: t("Kostenlose Schliessfächer"),
                    },
                    {
                        key: t("Attraktionen"),
                        value: t("Unbegrenzter Zugang zu allen Attraktionen ausser der Speed & Drift Zone"),
                    },
                    {
                        key: t("Getränk"),
                        value: t("Welcome-Drink (alkoholfrei)"),
                    },
                    {
                        key: t("Gastrobereich"),
                        value: t("Reservierte Zone im Gastrobereich"),
                    },
                    {
                        key: t("Competition"),
                        value: t("Eine Competition der Kategorie A"),
                    },
                    {
                        key: t("Hosting"),
                        value: t("Welcome-Hosting"),
                    },
                    {
                        key: t("Gewinnspiel"),
                        value: t("Kostenlose Teilnahme am Gewinnspiel am Ende des Besuchs"),
                    },
                    {
                        key: t("Gruppenfoto"),
                        value: t("Ausgedrucktes Gruppenfoto sowie Versand per E-Mail"),
                    },
                    {
                        key: t("Verfügbarkeit"),
                        value: t("Montag bis Sonntag"),
                    },
                    {
                        key: t("Credits"),
                        value: t("Nicht verbrauchte Credits bleiben für den nächsten Besuch erhalten"),
                    },
                    {
                        key: t("Übertragbarkeit"),
                        value: t("Credits sind personengebunden und nicht auf andere Personen übertragbar"),
                    },
                ],
            },
            deprecated: false,
            creditBased: true,
            extra: {
                releaseDate: "2026-08-19",
            },
        },
        {
            id: PackageCategory.NonPlusUltra,
            title: t("Non Plus Ultra"),
            description: (
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "Non Plus Ultra is the top experience for groups that do not want to make compromises. With a welcome drink, two competitions of your choice, personal VIP hosting and an attraction reserved exclusively for you, your event becomes an experience of the highest class. And if you want even more adrenaline in the Speed & Drift Zone, you can simply add credits. For groups that settle for nothing but the best."
                            ),
                        }}
                    />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />

                        <li>{t("Welcome drink (non-alcoholic)")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Reserved zone in the gastro area")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Two Category A or B competitions included")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Exclusive reservation option for an attraction of your choice")}} />
                        <li>
                            <b>{t("VIP hosting")}</b>
                        </li>
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />
                    </ul>
                </div>
            ),
            images: ["/img/new_category/non_plus_ultra_1.webp", "/img/new_category/non_plus_ultra_2.webp", "/img/new_category/non_plus_ultra_3.webp"],
            color: "red-400",
            price: 85,
            shortTitle: t("The ultimate VIP experience: 2 competitions and personalized VIP hosting."),
            tags: [
                {iconName: "persons", title: t("From 8 people")},
                {iconName: "calendar", title: t("Monday to Sunday")},
                {iconName: "activities", title: t("20+ attractions")},
            ],
            buttonText: t("Book now"),
            timeAvailableText: t("3, 4.5 or 8 hours"),
            showMoreDetail: false,
            enable: true,
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark."),
                    t("Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF."),
                    t("Please note that cash payment is not possible on-site, and attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("Gruppengrösse"),
                        value: t("Ab 8 Personen"),
                    },
                    {
                        key: t("Check-in"),
                        value: t("Easy Access & Priority Check-in"),
                    },
                    {
                        key: t("Schliessfächer"),
                        value: t("Kostenlose Schliessfächer"),
                    },
                    {
                        key: t("Attraktionen"),
                        value: t("Unbegrenzter Zugang zu allen Attraktionen ausser der Speed & Drift Zone"),
                    },
                    {
                        key: t("Getränk"),
                        value: t("Welcome-Drink (alkoholfrei)"),
                    },
                    {
                        key: t("Gastrobereich"),
                        value: t("Reservierte Zone im Gastrobereich"),
                    },
                    {
                        key: t("Competitions"),
                        value: t("Zwei Competitions der Kategorie A oder B"),
                    },
                    {
                        key: t("Hosting"),
                        value: t("Welcome-Hosting"),
                    },
                    {
                        key: t("Gewinnspiel"),
                        value: t("Kostenlose Teilnahme am Gewinnspiel am Ende des Besuchs"),
                    },
                    {
                        key: t("Gruppenfoto"),
                        value: t("Ausgedrucktes Gruppenfoto sowie Versand per E-Mail"),
                    },
                    {
                        key: t("Verfügbarkeit"),
                        value: t("Montag bis Sonntag"),
                    },
                    {
                        key: t("Credits"),
                        value: t("Nicht verbrauchte Credits bleiben für den nächsten Besuch erhalten"),
                    },
                    {
                        key: t("Übertragbarkeit"),
                        value: t("Credits sind personengebunden und nicht auf andere Personen übertragbar"),
                    },
                ],
            },
            deprecated: false,
            creditBased: true,
            extra: {
                releaseDate: "2026-08-19",
            },
        },
        {
            id: PackageCategory.HappyKids,
            title: t("Happy Kids"),
            description: (
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "Happy Kids takes the entire birthday organisation off your hands: decorations are ready, the table is reserved and the birthday child discovers more than 20 attractions with their guests. You just bring the cake — we take care of the rest. Anyone who wants extra action in the Speed & Drift Zone can add credits, which the whole group shares."
                            ),
                        }}
                    />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Reserved zone in the gastro area")}} />
                        <li className={"font-bold"}>{t("Birthday decorations")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Right to bring your own cake")}} />
                        <li className={"font-bold"}>{t("Welcome hosting")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Up to 50% cheaper: exclusive access to the large credit packs for the Speed & Drift Zone")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Shared credit balance — credits transferable within the group")}} />
                    </ul>
                </div>
            ),
            images: ["/img/new_category/happy_kids_1.webp", "/img/new_category/happy_kids_2.webp", "/img/new_category/happy_kids_3.webp"],
            color: "red-400",
            price: 37.5,
            shortTitle: t("The children's birthday party where all you have to bring is the cake."),
            tags: [
                {iconName: "persons", title: t("From 5 people")},
                {iconName: "persons", title: t("Minimum 1 adult")},
                {iconName: "calendar", title: t("Monday to Sunday")},
                {iconName: "percent", title: t("Children up to 15.9 years: 50% discount")},
            ],
            buttonText: t("Book now"),
            timeAvailableText: t("3, 4.5 hours"),
            showMoreDetail: false,
            priceImages: [
                {
                    image: "/img/new_category/Fun/gelb.webp",
                    alt: "",
                },
                {
                    image: "/img/new_category/Fun/rot.webp",
                    alt: "",
                },
            ],
            enable: true,
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark."),
                    t("Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF."),
                    t("Please note that cash payment is not possible on-site, and attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("Gruppengrösse"),
                        value: t("Ab 5 Personen"),
                    },
                    {
                        key: t("Check-in"),
                        value: t("Easy Access & Priority Check-in"),
                    },
                    {
                        key: t("Schliessfächer"),
                        value: t("Kostenlose Schliessfächer"),
                    },
                    {
                        key: t("Attraktionen"),
                        value: t("Unbegrenzter Zugang zu allen Attraktionen ausser der Speed & Drift Zone"),
                    },
                    {
                        key: t("Getränk"),
                        value: t("Geburtstagsdrink (alkoholfrei)"),
                    },
                    {
                        key: t("Gastrobereich"),
                        value: t("Reservierte Zone im Gastrobereich"),
                    },
                    {
                        key: t("Dekoration"),
                        value: t("Geburtstagsdekoration inklusive"),
                    },
                    {
                        key: t("Eigene Torte"),
                        value: t("Eine eigene Geburtstagstorte darf mitgebracht werden"),
                    },
                    {
                        key: t("Hosting"),
                        value: t("Welcome-Hosting"),
                    },
                    {
                        key: t("Gewinnspiel"),
                        value: t("Kostenlose Teilnahme am Gewinnspiel am Ende des Besuchs"),
                    },
                    {
                        key: t("Gruppenfoto"),
                        value: t("Ausgedrucktes Gruppenfoto sowie Versand per E-Mail"),
                    },
                    {
                        key: t("Verfügbarkeit"),
                        value: t("Montag bis Sonntag"),
                    },
                    {
                        key: t("Begleitperson"),
                        value: t("Mindestens eine erwachsene Begleitperson erforderlich"),
                    },
                    {
                        key: t("Personenzahl"),
                        value: t("Keine maximale Personenzahl"),
                    },
                    {
                        key: t("Kinderrabatt"),
                        value: t("Kinder unter 16 Jahren erhalten 50 % Rabatt"),
                    },
                    {
                        key: t("Credits"),
                        value: t("Nicht verbrauchte Credits bleiben für den nächsten Besuch erhalten"),
                    },
                    {
                        key: t("Übertragbarkeit"),
                        value: t("Credits können auf andere Personen übertragen werden"),
                    },
                ],
                infoEventPackage: {
                    title: t("In PACKAGE package included", {packageName: "Kinder "}),
                    values: [t("Balaclavas (with karting ticket)")],
                },
            },
            deprecated: false,
            creditBased: true,
            extra: {
                releaseDate: "2026-08-19",
            },
        },
        {
            id: PackageCategory.HappyKidsPlus,
            title: t("Happy Kids Plus"),
            description: (
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "Happy Kids Plus crowns the birthday: More than 20 attractions await the birthday child and guests. In addition, the whole group competes in one Category A competition — whether Grand Prix Classic, Interactive Mini Golf or Cube Challenge. If you want even more action afterwards, you can add credits for the Speed & Drift Zone, which the whole group uses together. Hardly any birthday can top that."
                            ),
                        }}
                    />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Reserved zone in the gastro area")}} />
                        <li className={"font-bold"}>{t("Birthday decorations")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Right to bring your own cake")}} />
                        <li className={"font-bold"}>{t("Welcome hosting")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />
                        <li>{t("Welcome drink (non-alcoholic)")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("One Category A competition included")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Up to 50% cheaper: exclusive access to the large credit packs for the Speed & Drift Zone")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Shared credit balance — credits transferable within the group")}} />
                    </ul>
                </div>
            ),
            images: ["/img/new_category/happy_kids_plus_1.webp", "/img/new_category/happy_kids_plus_2.webp", "/img/new_category/happy_kids_plus_3.webp"],
            color: "red-400",
            price: 37.5,
            shortTitle: t("Birthday party with an awards ceremony: A private competition included."),
            tags: [
                {iconName: "persons", title: t("From 5 people")},
                {iconName: "persons", title: t("Minimum 1 adult")},
                {iconName: "calendar", title: t("Monday to Sunday")},
                {iconName: "percent", title: t("Children up to 15.9 years: 50% discount")},
            ],
            buttonText: t("Book now"),
            timeAvailableText: t("3, 4.5 hours"),
            showMoreDetail: false,
            enable: true,
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark."),
                    t("Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF."),
                    t("Please note that cash payment is not possible on-site, and attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("Gruppengrösse"),
                        value: t("Ab 5 Personen"),
                    },
                    {
                        key: t("Check-in"),
                        value: t("Easy Access & Priority Check-in"),
                    },
                    {
                        key: t("Schliessfächer"),
                        value: t("Kostenlose Schliessfächer"),
                    },
                    {
                        key: t("Attraktionen"),
                        value: t("Unbegrenzter Zugang zu allen Attraktionen ausser der Speed & Drift Zone"),
                    },
                    {
                        key: t("Getränk"),
                        value: t("Geburtstagsdrink (alkoholfrei)"),
                    },
                    {
                        key: t("Gastrobereich"),
                        value: t("Reservierte Zone im Gastrobereich"),
                    },
                    {
                        key: t("Dekoration"),
                        value: t("Geburtstagsdekoration inklusive"),
                    },
                    {
                        key: t("Competition"),
                        value: t("Eine Competition der Kategorie A"),
                    },
                    {
                        key: t("Eigene Torte"),
                        value: t("Eine eigene Geburtstagstorte darf mitgebracht werden"),
                    },
                    {
                        key: t("Hosting"),
                        value: t("Welcome-Hosting"),
                    },
                    {
                        key: t("Gewinnspiel"),
                        value: t("Kostenlose Teilnahme am Gewinnspiel am Ende des Besuchs"),
                    },
                    {
                        key: t("Gruppenfoto"),
                        value: t("Ausgedrucktes Gruppenfoto sowie Versand per E-Mail"),
                    },
                    {
                        key: t("Verfügbarkeit"),
                        value: t("Montag bis Sonntag"),
                    },
                    {
                        key: t("Begleitperson"),
                        value: t("Mindestens eine erwachsene Begleitperson erforderlich"),
                    },
                    {
                        key: t("Personenzahl"),
                        value: t("Keine maximale Personenzahl"),
                    },
                    {
                        key: t("Kinderrabatt"),
                        value: t("Kinder unter 16 Jahren erhalten 50 % Rabatt"),
                    },
                    {
                        key: t("Credits"),
                        value: t("Nicht verbrauchte Credits bleiben für den nächsten Besuch erhalten"),
                    },
                    {
                        key: t("Übertragbarkeit"),
                        value: t("Credits können auf andere Personen übertragen werden"),
                    },
                ],
            },
            deprecated: false,
            creditBased: true,
            extra: {
                releaseDate: "2026-08-19",
            },
        },
        {
            id: PackageCategory.HappyParty,
            title: t("Happy Party"),
            description: (
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "Happy Party turns an evening into a shared experience: You start with a welcome drink of your choice, have your reserved area and enjoy more than 20 attractions as you please. If you want extra action in the Speed & Drift Zone, you can add credits. From four people — and the atmosphere takes care of itself."
                            ),
                        }}
                    />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li>{t("Welcome drink (with or without alcohol)")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Reserved zone in the gastro area")}} />
                        <li className={"font-bold"}>{t("Welcome hosting")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />
                    </ul>
                </div>
            ),
            images: ["/img/new_category/happy_party_1.webp", "/img/new_category/happy_party_2.webp", "/img/new_category/happy_party_3.webp"],
            color: "red-400",
            price: 69,
            shortTitle: t("Bachelorette party, corporate event, or just because: the party with a guaranteed kick-off."),
            tags: [
                {iconName: "persons", title: t("From 4 people")},
                {iconName: "calendar", title: t("Monday to Sunday")},
                {iconName: "activities", title: t("20+ attractions")},
            ],
            buttonText: t("Book now"),
            timeAvailableText: t("3, 4.5 or 8 hours"),
            showMoreDetail: false,
            enable: true,
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark."),
                    t("Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF."),
                    t("Please note that cash payment is not possible on-site, and attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("Gruppengrösse"),
                        value: t("Ab 4 Personen"),
                    },
                    {
                        key: t("Check-in"),
                        value: t("Easy Access & Priority Check-in"),
                    },
                    {
                        key: t("Schliessfächer"),
                        value: t("Kostenlose Schliessfächer"),
                    },
                    {
                        key: t("Attraktionen"),
                        value: t("Unbegrenzter Zugang zu allen Attraktionen ausser der Speed & Drift Zone"),
                    },
                    {
                        key: t("Getränk"),
                        value: t("Welcome-Drink (alkoholisch oder alkoholfrei)"),
                    },
                    {
                        key: t("Gastrobereich"),
                        value: t("Reservierte Zone im Gastrobereich"),
                    },
                    {
                        key: t("Hosting"),
                        value: t("Welcome-Hosting"),
                    },
                    {
                        key: t("Gewinnspiel"),
                        value: t("Kostenlose Teilnahme am Gewinnspiel am Ende des Besuchs"),
                    },
                    {
                        key: t("Gruppenfoto"),
                        value: t("Ausgedrucktes Gruppenfoto sowie Versand per E-Mail"),
                    },
                    {
                        key: t("Verfügbarkeit"),
                        value: t("Montag bis Sonntag"),
                    },
                    {
                        key: t("Credits"),
                        value: t("Nicht verbrauchte Credits bleiben für den nächsten Besuch erhalten"),
                    },
                    {
                        key: t("Übertragbarkeit"),
                        value: t("Credits sind personengebunden und nicht auf andere Personen übertragbar"),
                    },
                ],
            },
            deprecated: false,
            creditBased: true,
            extra: {
                releaseDate: "2026-08-19",
            },
        },
        {
            id: PackageCategory.HappyPartyPlus,
            title: t("Happy Party Plus"),
            description: (
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "Happy Party Plus brings competitive spirit to your event: More than 20 attractions and one Category A competition provide plenty of action. Form teams, compete, celebrate — the ranking will give you something to talk about and make your evening unforgettable. And if you want real adrenaline, you can add credits for the Speed & Drift Zone. For groups of eight or more who want the full experience."
                            ),
                        }}
                    />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li>{t("Welcome drink (with or without alcohol)")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Reserved zone in the gastro area")}} />
                        <li className={"font-bold"}>{t("Welcome hosting")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />

                        <li dangerouslySetInnerHTML={{__html: t("One Category A competition included")}} />
                    </ul>
                </div>
            ),
            images: ["/img/new_category/happy_party_plus_1.webp", "/img/new_category/happy_party_plus_2.webp", "/img/new_category/happy_party_plus_3.webp"],

            color: "red-400",
            price: 79,
            shortTitle: t("The party with its own competition: May the best win."),
            tags: [
                {iconName: "persons", title: t("From 8 people")},
                {iconName: "calendar", title: t("Monday to Sunday")},
                {iconName: "activities", title: t("20+ attractions")},
            ],
            buttonText: t("Book now"),
            timeAvailableText: t("3, 4.5 or 8 hours"),
            showMoreDetail: false,
            enable: true,
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark."),
                    t("Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF."),
                    t("Please note that cash payment is not possible on-site, and attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("Gruppengrösse"),
                        value: t("Ab 8 Personen"),
                    },
                    {
                        key: t("Check-in"),
                        value: t("Easy Access & Priority Check-in"),
                    },
                    {
                        key: t("Schliessfächer"),
                        value: t("Kostenlose Schliessfächer"),
                    },
                    {
                        key: t("Attraktionen"),
                        value: t("Unbegrenzter Zugang zu allen Attraktionen ausser der Speed & Drift Zone"),
                    },
                    {
                        key: t("Getränk"),
                        value: t("Welcome-Drink (alkoholisch oder alkoholfrei)"),
                    },
                    {
                        key: t("Gastrobereich"),
                        value: t("Reservierte Zone im Gastrobereich"),
                    },
                    {
                        key: t("Competition"),
                        value: t("Eine Competition der Kategorie A"),
                    },
                    {
                        key: t("Hosting"),
                        value: t("Welcome-Hosting"),
                    },
                    {
                        key: t("Gewinnspiel"),
                        value: t("Kostenlose Teilnahme am Gewinnspiel am Ende des Besuchs"),
                    },
                    {
                        key: t("Gruppenfoto"),
                        value: t("Ausgedrucktes Gruppenfoto sowie Versand per E-Mail"),
                    },
                    {
                        key: t("Verfügbarkeit"),
                        value: t("Montag bis Sonntag"),
                    },
                    {
                        key: t("Credits"),
                        value: t("Nicht verbrauchte Credits bleiben für den nächsten Besuch erhalten"),
                    },
                    {
                        key: t("Übertragbarkeit"),
                        value: t("Credits sind personengebunden und nicht auf andere Personen übertragbar"),
                    },
                ],
            },
            deprecated: false,
            creditBased: true,
            extra: {
                releaseDate: "2026-08-19",
            },
        },
        {
            id: PackageCategory.HappyPartyVIP,
            title: t("Happy Party VIP"),
            description: (
                <div>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t(
                                "With Happy Party VIP, your event becomes an exclusive group experience: A welcome drink of your choice, personal VIP hosting and two competitions make your evening unique. It also includes one attraction reserved exclusively for you — for a group experience that stands out. If you want even more speed, you can add credits for the Speed & Drift Zone."
                            ),
                        }}
                    />
                    <br />
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li>{t("Welcome drink (with or without alcohol)")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Reserved zone in the gastro area")}} />
                        <li className={"font-bold"}>{t("VIP hosting")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Free entry into the prize draw")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Two Category A or B competitions included")}} />
                        <li dangerouslySetInnerHTML={{__html: t("Exclusive reservation option for an attraction of your choice")}} />
                    </ul>
                </div>
            ),
            images: ["/img/new_category/happy_party_vip_1.webp", "/img/new_category/happy_party_vip_2.webp", "/img/new_category/happy_party_vip_3.webp"],

            color: "red-400",
            price: 89,
            shortTitle: t("Special moments deserve VIP treatment."),
            tags: [
                {iconName: "persons", title: t("From 8 people")},
                {iconName: "calendar", title: t("Monday to Sunday")},
                {iconName: "activities", title: t("20+ attractions")},
            ],
            buttonText: t("Book now"),
            timeAvailableText: t("3, 4.5 or 8 hours"),
            showMoreDetail: false,
            enable: true,
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark."),
                    t("Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF."),
                    t("Please note that cash payment is not possible on-site, and attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("Gruppengrösse"),
                        value: t("Ab 8 Personen"),
                    },
                    {
                        key: t("Check-in"),
                        value: t("Easy Access & Priority Check-in"),
                    },
                    {
                        key: t("Schliessfächer"),
                        value: t("Kostenlose Schliessfächer"),
                    },
                    {
                        key: t("Attraktionen"),
                        value: t("Unbegrenzter Zugang zu allen Attraktionen ausser der Speed & Drift Zone"),
                    },
                    {
                        key: t("Getränk"),
                        value: t("Welcome-Drink (alkoholisch oder alkoholfrei)"),
                    },
                    {
                        key: t("Gastrobereich"),
                        value: t("Reservierte Zone im Gastrobereich"),
                    },
                    {
                        key: t("Competitions"),
                        value: t("Zwei Competitions der Kategorie A oder B"),
                    },
                    {
                        key: t("Exklusive Reservation"),
                        value: t("Exklusive Reservationsmöglichkeit für eine Zone nach Wahl"),
                    },
                    {
                        key: t("Hosting"),
                        value: t("VIP-Hosting"),
                    },
                    {
                        key: t("Gewinnspiel"),
                        value: t("Kostenlose Teilnahme am Gewinnspiel am Ende des Besuchs"),
                    },
                    {
                        key: t("Gruppenfoto"),
                        value: t("Ausgedrucktes Gruppenfoto sowie Versand per E-Mail"),
                    },
                    {
                        key: t("Verfügbarkeit"),
                        value: t("Montag bis Sonntag"),
                    },
                    {
                        key: t("Credits"),
                        value: t("Nicht verbrauchte Credits bleiben für den nächsten Besuch erhalten"),
                    },
                    {
                        key: t("Übertragbarkeit"),
                        value: t("Credits sind personengebunden und nicht auf andere Personen übertragbar"),
                    },
                ],
            },
            deprecated: false,
            creditBased: true,
            extra: {
                releaseDate: "2026-08-19",
            },
        },
        {
            id: PackageCategory.SchoolTrophy,
            title: t("School-Trophy"),
            description: (
                <div>
                    <ul className={clsx("list-disc pl-5")}>
                        <li dangerouslySetInnerHTML={{__html: t("Easy access and Priority Check-in")}} />
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li>{t("The accompanying person receives free admission (one additional free accompanying person per 25 children).")}</li>

                        <li>
                            {t("Children under 18 years of age receive")}
                            &nbsp;
                            <b>{t("25% discount on the ticket price")}</b>
                        </li>
                        <li>{t("On special request (events@arbopark.ch), larger groups can also be received outside opening hours.")}</li>
                    </ul>
                    <br />
                    <span>{t("Schools and associations can book this package ")}</span>
                    <b>{t("every day that ArboPark is open")}</b>. {t("It's the perfect package for groups to experience fun and action together!")}
                </div>
            ),
            timeAvailableText: t("2, 3 or 4.5 hours"),
            images: ["/img/new_category/standard_1.webp", "/img/new_category/standard_2.webp", "/img/new_category/standard_3.webp"],
            color: "red-400",
            price: 25.25,
            shortTitle: t("The ideal offer for schools and associations!"),
            tags: [
                {iconName: "calendar", title: t("Monday till Friday")},
                {
                    iconName: "persons",
                    title: t("personsminimum", {count: 35}),
                },
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("Book now"),
            showMoreDetail: false,
            priceImages: [
                {
                    image: "/assets/images/package_category/details/gelb.webp",
                    alt: "",
                },
                {
                    image: "/assets/images/package_category/details/rot.webp",
                    alt: "",
                },
            ],
            extra: {
                minDaysFromNow: 1,
                maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
            },
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Additional kart rides can be purchased for 10 CHF at the kart track for both types of tickets, if capacity is available."),
                    t("Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)"),
                    t("Please note that cash payments are not possible on site. Attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("All children aged 8 to 15 years get 25% discount"),
                        value: "",
                    },
                    {
                        key: t("With and without karting possible in the same reservation"),
                        value: "",
                    },
                    {
                        key: t("The ticket is valid every day"),
                        value: "",
                    },
                    {
                        key: t("Min. 1 adult (at regular rate)"),
                        value: "",
                    },
                ],
            },
            enable: false,
            deprecated: false,
        },
        {
            id: PackageCategory.MegaFunSchool,
            title: t("Mega Fun School Package"),
            description: (
                <div>
                    <ul className={"list-disc pl-5"}>
                        <li>
                            <b>{t("Ease Access")}</b>
                        </li>
                        <li className={"font-bold"}>{t("Free Lockers")}</li>
                        <li dangerouslySetInnerHTML={{__html: t("Unlimited access to all attractions except the Speed & Drift Zone")}} />
                        <li>{t("Children until 17.9 years old")}</li>
                        <li
                            dangerouslySetInnerHTML={{
                                __html: t("Adults pay the same price as children, with a maximum of 25% adult share."),
                            }}
                        ></li>

                        {/*<li>*/}
                        {/*    <b>{t("Category A Competition")}</b> {t("on tickets with 3 and 4.5 hours")}*/}
                        {/*</li>*/}
                        <li>
                            <b>{t("1 Shorly per Person gratis")}</b> {t("in the restaurant area")}
                        </li>

                        <li>
                            <b>{t("Welcome host")}</b>
                        </li>
                        <li dangerouslySetInnerHTML={{__html: t("Printed group photo + by email")}} />

                        <li>{t("On special request (events@arbopark.ch), larger groups can also be received outside opening hours.")}</li>
                        <li>
                            {t("For tickets with karting")}: <b>{t("free helmet")}</b> {t("at the kart track")}
                        </li>
                    </ul>
                    <br />
                    <span>{t("Schools and associations can book this package ")}</span>
                    <b>{t("book Monday till Friday")}</b>. {t("It's the perfect package for groups to experience fun and action together!")}
                </div>
            ),
            timeAvailableText: t("2, 3 or 4.5 hours"),
            images: ["/img/new_category/school_verein_1.webp", "/img/new_category/school_verein_2.webp", "/img/new_category/school_verein_3.webp"],
            color: "red-400",
            price: 29.75,
            shortTitle: t("The ideal offer for schools and associations!"),
            tags: [
                {iconName: "calendar", title: t("Monday till Friday")},
                {iconName: "persons", title: t("personsminimum", {count: 10})},
                {iconName: "activities", title: t("20+ Attractions")},
            ],
            buttonText: t("Book now"),
            showMoreDetail: false,
            priceImages: [
                {
                    image: "/img/new_category/details/gelb.webp",
                    alt: "",
                },
                {
                    image: "/img/new_category/details/rot.webp",
                    alt: "",
                },
            ],
            extra: {
                minDaysFromNow: 1,
                maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
            },
            categoryInfo: {
                upsell_percent: 0,
                title: t("Group offer"),
                subtitle: t("ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "),
                infoText: [
                    t("Additional kart rides can be purchased for PRICE CHF at the kart track for both types of tickets, if capacity is available.", {
                        price: 10,
                    }),
                    t("Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)"),
                    t("Please note that cash payments are not possible on site. Attractions cannot be reserved."),
                ],
                infoValues: [
                    {
                        key: t("All children aged 8 to 17.9 years"),
                        value: "",
                    },
                    {
                        key: t("Maximum 25% Adults allowed"),
                        value: "",
                    },
                    {
                        key: t("The School / Club Ticket is available in two versions: red and yellow"),
                        value: "",
                    },
                    {
                        key: t("With and without karting possible in the same reservation"),
                        value: "",
                    },
                    {
                        key: t("The ticket is valid every day from Monday till Friday"),
                        value: "",
                    },
                    {
                        key: t("Min. 1 adult (at regular rate)"),
                        value: "",
                    },
                ],
            },
            enable: true,
            deprecated: false,
        },
    ];

    const packageCategoryCardHandle = (categorySlug: PackageCategory) => {
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                category: categorySlug,
            })
        );

        if (categorySlug === PackageCategory.Giftcard) {
            return history.push("/booking/package/5");
        }

        if (categorySlug === PackageCategory.Yearly) {
            return history.push("/booking/package/6");
        }

        if (categorySlug === PackageCategory.ThreeTwo) {
            return history.push("/booking/package/21");
        }

        if (categorySlug === PackageCategory.Event) {
            return history.push("/booking/events/subcategory" + "?" + appendQueryParams(bookingFilter));
        }

        if (categorySlug === PackageCategory.Ferien) {
            return history.push("/booking/ferien/subcategory" + "?" + appendQueryParams(bookingFilter));
        }

        // initCart and remove booking process
        dispatch<ThunkAction<any, any, any, any>>(BookingAction.initCart()).then(r => {
            // dispatch(BookingAction.clearBookingProcess());
            return history.push("/booking/options" + "?" + appendQueryParams(bookingFilter));
        });
    };

    const getCategoryBySlug = (slug: PackageCategory): PackageCategoryEntity => {
        const fallbackCategory = categories[0];

        // Utility function to find category in a given list
        const findCategory = (list: PackageCategoryEntity[]): PackageCategoryEntity | null => {
            const index = list.findIndex(cat => cat.id === slug);
            return index !== -1 ? list[index] : null;
        };

        // Check each category list in order of priority
        return findCategory(categories) || findCategory(eventsSubcategories) || findCategory(ferienSubcategories) || fallbackCategory;
    };

    /**
     * Determines the drivers count text status for a given package category and person type.
     *
     * @param {PackageCategory} category - The package category to evaluate.
     * @param {PersonType} personType - The type of person (e.g., Adult, Child, etc.) used for evaluation.
     * @returns {boolean} - Returns `false` if the person type is `Adult`.
     *                      Returns `true` if the package category is `PackageCategory.FerienPassB` and
     *                      the person type is not `Adult`. Otherwise, returns `false`.
     */
    const getPackageCategoryDriversCountText = (category: PackageCategory, personType: PersonType): boolean => {
        if (personType == PersonType.Adult) {
            return false;
        }
        return category === PackageCategory.FerienPassB;
    };
    return {
        categories,
        eventsSubcategories,
        ferienSubcategories,
        getCategoryBySlug,
        getPackageCategoryDriversCountText,
        packageCategoryCardHandle,
    };
};
